import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  sitemap: {
    siteUrl: "https://api.link.sc",
    changefreq: "weekly",
    priority: 0.7,
  },
  site: {
    title: "link.sc API",
    logo: {
      src: {
        light: "/logo-light.svg",
        dark: "/logo-dark.svg",
      },
      alt: "link.sc",
      width: "120px",
    },
    showPoweredBy: false,
    footer: {
      columns: [
        {
          title: "Product",
          links: [
            { label: "Features", href: "https://link.sc/#features" },
            { label: "Pricing", href: "https://link.sc/#pricing" },
            { label: "Documentation", href: "https://link.sc/docs" },
            { label: "Blog", href: "https://link.sc/blog" },
          ],
        },
        {
          title: "APIs",
          links: [
            { label: "Fetch API", href: "/api" },
            { label: "Search API", href: "/api" },
            { label: "Playground", href: "https://link.sc/dashboard/playground" },
          ],
        },
        {
          title: "Resources",
          links: [
            { label: "Dashboard", href: "https://link.sc/dashboard" },
            { label: "Changelog", href: "https://link.sc/docs/changelog" },
            { label: "GitHub", href: "https://github.com/getlinksc" },
          ],
        },
      ],
      social: [
        { icon: "github", href: "https://github.com/getlinksc" },
      ],
      copyright: `\u00a9 ${new Date().getFullYear()} link.sc Infrastructure Inc.`,
    },
  },
  metadata: {
    title: "link.sc API Reference",
    description:
      "API reference for link.sc \u2014 web fetch and search API optimized for LLMs.",
  },
  theme: {
    fonts: {
      sans: "Inter",
      mono: "JetBrains Mono",
    },
    dark: {
      background: "#0d1512",
      foreground: "#fafaf8",
      card: "#101b15",
      cardForeground: "#fafaf8",
      popover: "#101b15",
      popoverForeground: "#fafaf8",
      primary: "#4ba383",
      primaryForeground: "#ffffff",
      secondary: "#26312a",
      secondaryForeground: "#fafaf8",
      muted: "#26312a",
      mutedForeground: "#9aa59d",
      accent: "#26312a",
      accentForeground: "#fafaf8",
      destructive: "#ef4444",
      destructiveForeground: "#ffffff",
      border: "#26312a",
      input: "#26312a",
      ring: "#4ba383",
      radius: "0.75rem",
    },
    light: {
      background: "#fafaf8",
      foreground: "#141715",
      card: "#ffffff",
      cardForeground: "#26312a",
      popover: "#ffffff",
      popoverForeground: "#26312a",
      primary: "#0f6b4e",
      primaryForeground: "#ffffff",
      secondary: "#e7f2ec",
      secondaryForeground: "#26312a",
      muted: "#f1f3ef",
      mutedForeground: "#5c645e",
      accent: "#e7f2ec",
      accentForeground: "#26312a",
      destructive: "#dc2626",
      destructiveForeground: "#ffffff",
      border: "#e6e8e4",
      input: "#e6e8e4",
      ring: "#0f6b4e",
      radius: "0.75rem",
    },
    customCss: `
      /* ── Shared base styles ── */
      header {
        backdrop-filter: blur(12px) !important;
        -webkit-backdrop-filter: blur(12px) !important;
      }

      pre, .shiki, code[class*="language-"] {
        border-radius: 0.75rem;
      }

      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { border-radius: 4px; }

      main::before {
        content: '';
        position: fixed;
        top: -200px;
        left: 50%;
        transform: translateX(-50%);
        width: 800px;
        height: 500px;
        pointer-events: none;
        z-index: -1;
      }

      /* ── Dark theme ── */
      [data-theme="dark"] header {
        background: rgba(13, 21, 18, 0.8) !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
      }

      [data-theme="dark"] pre,
      [data-theme="dark"] .shiki,
      [data-theme="dark"] code[class*="language-"] {
        background: #101b15 !important;
        border: 1px solid rgba(255, 255, 255, 0.05);
      }

      [data-theme="dark"] a:not([class]):not([role]) {
        text-decoration-color: rgba(75, 163, 131, 0.4);
      }
      [data-theme="dark"] a:not([class]):not([role]):hover {
        text-decoration-color: #4ba383;
      }

      [data-theme="dark"] th { background: #101b15; }
      [data-theme="dark"] table,
      [data-theme="dark"] th,
      [data-theme="dark"] td { border-color: rgba(255, 255, 255, 0.05); }

      [data-theme="dark"] ::-webkit-scrollbar-thumb { background: #26312a; }
      [data-theme="dark"] ::-webkit-scrollbar-thumb:hover { background: #3a4a40; }

      [data-theme="dark"] main::before {
        background: radial-gradient(ellipse, rgba(75, 163, 131, 0.08), transparent 70%);
      }

      [data-theme="dark"] nav ul > li + li {
        border-top-color: rgba(255, 255, 255, 0.05);
      }

      [data-theme="dark"] footer {
        border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
      }

      /* ── Light theme ── */
      [data-theme="light"] header {
        background: #ffffff !important;
        border-bottom: 1px solid #e6e8e4 !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
      }

      [data-theme="light"] aside {
        background: #ffffff !important;
      }

      [data-theme="light"] aside nav,
      [data-theme="light"] aside ul {
        background: transparent !important;
      }

      [data-theme="light"] aside a[aria-current="page"],
      [data-theme="light"] aside a[data-active="true"] {
        color: #0f6b4e !important;
        background: #e7f2ec !important;
      }

      [data-theme="light"] pre,
      [data-theme="light"] .shiki,
      [data-theme="light"] code[class*="language-"] {
        background: #fafaf8 !important;
        border: 1px solid #e6e8e4;
      }

      [data-theme="light"] a:not([class]):not([role]) {
        color: #0f6b4e;
        text-decoration-color: rgba(75, 163, 131, 0.3);
      }
      [data-theme="light"] a:not([class]):not([role]):hover {
        text-decoration-color: #0f6b4e;
      }

      [data-theme="light"] th { background: #f1f3ef; }
      [data-theme="light"] table,
      [data-theme="light"] th,
      [data-theme="light"] td { border-color: #e6e8e4; }

      [data-theme="light"] ::-webkit-scrollbar-thumb { background: #c7cdc7; }
      [data-theme="light"] ::-webkit-scrollbar-thumb:hover { background: #9aa59d; }

      [data-theme="light"] main::before {
        display: none;
      }

      [data-theme="light"] nav ul > li + li {
        border-top-color: #e6e8e4;
      }

      [data-theme="light"] footer {
        border-top: 1px solid #e6e8e4 !important;
        background: #fafaf8 !important;
      }
    `,
  },
  redirects: [
    { from: "/", to: "/api" },
    { from: "/api", to: "/api/core" },
  ],
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  apis: {
    type: "file",
    input: "./openapi.json",
    path: "/api",
    options: {
      examplesLanguage: "curl",
      expandAllTags: true,
      expandApiInformation: true,
      schemaDownload: { enabled: true },
    },
  },
  defaults: {
    examplesLanguage: "curl",
    apis: {
      expandAllTags: true,
    },
  },
  navigation: [
    {
      type: "link",
      label: "API Reference",
      to: "/api",
      icon: "code",
    },
    {
      type: "link",
      label: "MCP Server",
      to: "/mcp",
      icon: "plug",
    },
    {
      type: "link",
      label: "Docs",
      to: "https://link.sc/docs",
      icon: "book-open",
    },
    {
      type: "link",
      label: "Blog",
      to: "https://link.sc/blog",
      icon: "newspaper",
    },
    {
      type: "link",
      label: "Dashboard",
      to: "https://link.sc/dashboard",
      icon: "layout-dashboard",
    },
  ],
};

export default config;
